import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../../core/i18n'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { agentService, utilService } from '../../../../core/services'
import * as interfaces from '../../../../static/interfaces'
import * as Hooks from '../../../../utils/hooks'
import { Button } from '../../../atoms'
import { EditTable } from '../../../organisms'
import { Panel } from '../../../atoms'
import SidebarLayout from '../../_layouts/sidebarLayout'
import { getPostName } from 'utils/soundoutOrFormalRequest'

declare var window: interfaces.IWindow

interface IProps {
  templates: {
    sound_out_templates: IJsonResponse
  }
  user: any
}

const COLUMNS = [
  {
    name: I18n.t('generic.name'),
    field: 'title',
    required: true,
  },
  {
    name: I18n.t('mail_template.subject'),
    field: 'mail_subject',
    required: true,
  },
  {
    name: I18n.t('generic.text'),
    field: 'message',
    required: true,
    renderElement: record => <div className="message">{record.message}</div>,
  },
  {
    name: I18n.t('agent_rank.kind'),
    field: 'agent_rank_id',
    required: false,
    renderElement: record =>
      record.agent_rank_id ? I18n.t('agent_rank.kind_special') : I18n.t('agent_rank.kind_normal'),
  },
  {
    name: I18n.t('generic.tour'),
    field: 'posts_short_names',
    required: false,
    renderElement: record => (
      <div className="postLinks">
        {record.posts.map((p, index) => (
          <div className="postLink" key={p.id}>
            <a href={`/agent/posts/${p.id}/edit`}>{getPostName(p)}</a>
          </div>
        ))}
      </div>
    ),
  },
]

const AgentMailTemplatesIndex: React.FC<IProps> = props => {
  Hooks.SetDocumentTitle('Formal Request Mail')
  const { data: templatesList, pagination } = agentService.getDataFromJson(
    props.templates.sound_out_templates
  )
  const { data: userData } = utilService.getDataFromJson(props.user)

  const [templates, setTemplates] = React.useState(templatesList)
  const handleClickDelete = record => {
    if (window.confirm(I18n.t('generic.delete_confirmation'))) {
      handleDelete(record.id)
    }
  }
  const handleDelete = React.useCallback(async id => {
    const { flush } = await agentService.deleteSoundOutTemplate(id)
    window.flashMessages.addMessage({
      text: flush.message,
      type: flush.type,
    })
    setTemplates(old => old.filter(template => template.id !== id))
  }, [])

  const renderRecordControls = React.useCallback(
    record => (
      <>
        {record.posts === '' && (
          <Button key="custom" small={true}>
            <a href={`/agent/sound_out_templates/${record.id}/customize`}>
              {I18n.t('generic.customize')}
            </a>
          </Button>
        )}
        {record.posts !== '' && (
          <>
            <Button key="edit" small={true}>
              <a href={`/agent/sound_out_templates/${record.id}/edit`}>{I18n.t('generic.edit')}</a>
            </Button>
            <Button key="delete" small={true} handleClick={() => handleClickDelete(record)}>
              {I18n.t('generic.delete')}
            </Button>
          </>
        )}
      </>
    ),
    []
  )

  return (
    <SidebarLayout user={userData}>
      <Wrapper>
        <Panel
          title={
            <Heading>
              <span>{I18n.t('generic.sound_out_template')}</span>
              <Button small={true}>
                <a href="/agent/sound_out_templates/new">{I18n.t('generic.create_new')}</a>
              </Button>
            </Heading>
          }
        >
          <EditTable
            editable={true}
            columns={COLUMNS}
            records={templates}
            pagination={pagination}
            handleDelete={null}
            getEditLink={null}
            renderRecordControls={renderRecordControls}
          />
        </Panel>
      </Wrapper>
    </SidebarLayout>
  )
}

const Wrapper = styled.div`
  width: 100%;
  padding: 22px 40px 80px 46px;

  .Panel {
    max-width: none;
  }

  .postLinks {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;

    .postLink::after {
      content: ', ';
    }

    .postLink:last-child::after {
      content: '';
    }
  }

  .message {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px;
  }
`

const Heading = styled.div`
  .Button {
    margin-left: 12px;
  }
`

export default AgentMailTemplatesIndex
